import { createFileRoute } from "@tanstack/react-router";
import { DICTS } from "@/lib/i18n";
import { SiteShell } from "@/components/SiteShell";
import { HomeBlocksLayout } from "@/components/home/HomeBlocksLayout";
import { getActiveHeroCampaign } from "@/lib/admin/heroCampaigns.functions";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { storageImage, storageSrcSet } from "@/lib/image-url";
import { cachedSSR } from "@/lib/ssrCache";
import {
  deriveHeroLoaderData,
  EMPTY_HERO,
  type HeroLoaderData,
} from "@/lib/heroLoaderMedia";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/")({
  headers: publicCacheHeaders,
  // ⚡ LCP: نجلب صورة الهيرو على السيرفر لنحقن preload في الـhead
  // (السوق الافتراضي AE؛ الحملات لباقي الأسواق تُحمَّل عادةً من الكلاينت).
  // نقرأ حملة كلّ مقاس على حدة وبالتوازي — نفس اختيار العميل بالضبط، فلا
  // preload لأصل جهاز غير معروض.
  // ⚡ TTFB: عبر cachedSSR — أوّل بايت لا ينتظر قاعدة البيانات بعد أوّل زيارة.
  loader: async (): Promise<HeroLoaderData> =>
    cachedSSR<HeroLoaderData>(
      "home-hero:AE:by-device",
      async () => {
        const [mobileCampaign, desktopCampaign] = await Promise.all([
          getActiveHeroCampaign({ data: { market: "AE", page: "home", device: "mobile" } }),
          getActiveHeroCampaign({ data: { market: "AE", page: "home", device: "desktop" } }),
        ]);
        return deriveHeroLoaderData(mobileCampaign, desktopCampaign);
      },
      EMPTY_HERO,
    ),



  head: ({ loaderData }) => ({
    meta: [
      { title: DICTS.en["home.meta.title"] },
      { name: "description", content: DICTS.en["home.meta.description"] },
      { property: "og:title", content: DICTS.en["home.meta.title"] },
      { property: "og:description", content: DICTS.en["home.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/` },
      { property: "og:type", content: "website" },
      ...(loaderData?.heroPoster
        ? [
            { property: "og:image", content: loaderData.heroPoster },
            { name: "twitter:image", content: loaderData.heroPoster },
          ]
        : []),
    ],
    links: [
      { rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/` },
      // ⚡ LCP: تحميل مسبق لنسخة الموبايل المصغّرة ونسخة الديسكتوب، كلٌّ بوسيطه
      ...(loaderData?.mobileImage
        ? [
            {
              rel: "preload",
              as: "image",
              href: storageImage(loaderData.mobileImage, 750),
              imageSrcSet: storageSrcSet(loaderData.mobileImage, [420, 560, 750, 1000]),
              imageSizes: "100vw",
              media: "(max-width: 767px)",
              fetchPriority: "high" as const,
            },
          ]
        : []),
      ...(loaderData?.desktopImage
        ? [
            {
              rel: "preload",
              as: "image",
              href: storageImage(loaderData.desktopImage, 1440),
              imageSrcSet: storageSrcSet(loaderData.desktopImage, [1024, 1440, 1920]),
              imageSizes: "100vw",
              media: "(min-width: 768px)",
              fetchPriority: "high" as const,
            },
          ]
        : []),
      ...(loaderData?.heroVideo
        ? [{ rel: "preload", as: "video", href: loaderData.heroVideo, fetchPriority: "high" as const }]
        : []),
    ],



    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Lecmo",
          url: PUBLIC_SITE_ORIGIN,
          logo: `${PUBLIC_SITE_ORIGIN}/icon-512.png`,
          description: DICTS.en["home.meta.description"],
          sameAs: [
            "https://www.instagram.com/lecmo.perfume",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+971508877701",
            contactType: "customer service",
            availableLanguage: ["Arabic", "English"],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Lecmo",
          url: PUBLIC_SITE_ORIGIN,
          potentialAction: {
            "@type": "SearchAction",
            target: `${PUBLIC_SITE_ORIGIN}/shop?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  component: HomePage,
});


function HomePage() {
  const loaderData = Route.useLoaderData();
  return (
    <SiteShell>
      <HomeBlocksLayout
        initialHero={{
          mobileImage: loaderData?.mobileImage ?? null,
          desktopImage: loaderData?.desktopImage ?? null,
        }}
      />
    </SiteShell>
  );
}
